import React, { useState, useEffect, useRef, Fragment } from "react";
import * as httpService from "../services/httpService";
import * as userService from "../utils/userService";
import { Employee_Api_EndPoints } from "../global/end-points";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LoadingSpinner from "../components/common/loadingSpinner";
import { useTranslation } from "react-i18next";
import ExceptionPopup from "../components/common/exceptionhandle";
import useActivityLogger from "../utils/useActivityLogger";

function DoughSummary() {
  const { t } = useTranslation();
  const [isLoad, setIsLoad] = useState(false);
  const [rangeValue, setRangeValue] = useState(0);
  const [cashSummary, setCashSummary] = useState({
    wages: 0,
    tip: 0,
    grossPay: 0,
    netPay: 0,
  });

  const { wages, tip, netPay } = cashSummary;

  const [availableWagesFromRafil, setAvailableWagesFromRafil] = useState(0);
  const [availableTipsFromRafil, setAvailableTipsFromRafil] = useState(0);
  const [hasCard, setHasCard] = useState(null);
  const [cardLast4, setCardLast4] = useState("");
  const [showCardLast4Input, setShowCardLast4Input] = useState(false);
  const [isPayPeriodCompleted, setIsPayPeriodCompleted] = useState(false);
  const didMount = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  const [isException, setIsException] = useState(false);
  const [exceptionMsg, setExceptionMsg] = useState({
    title: "warning",
    content: "This is a sample content",
  });

  // ✅ Always prefer OnBehalfUserId if present, else fallback to UserId
  const effectiveUserId =
    sessionStorage.getItem("OnBehalfUserId") || sessionStorage.getItem("UserId");

  // ✅ Check if OBO mode
  const isOBO = !!sessionStorage.getItem("OnBehalfUserId");

  // Min card load amount from backend AppConfig (GET /api/AppConfig/GetMinCardLoadAmount); fallback 5 if API fails
  const [minimumLoadAmount, setMinimumLoadAmount] = useState(5);

  // ✅ Activity Logger
  const { logActivity, logClick, logApiCall } = useActivityLogger({
    moduleCode: "DPY",
  });

  // Min card load amount from backend (GET /api/AppConfig/GetMinCardLoadAmount); uses AccessToken like other secured endpoints
  const fetchMinCardLoadAmount = () => {
    const token = userService.GetUserToken();
    httpService
      .CallWithHeaders(
        "get",
        Employee_Api_EndPoints.GET_MIN_CARD_LOAD_AMOUNT,
        {},
        { AccessToken: token || "" }
      )
      .then((resp) => {
        if (resp && resp.status === 200 && resp.data) {
          const n = Number(resp.data.minAmount);
          if (Number.isFinite(n) && n > 0) setMinimumLoadAmount(n);
        }
      })
      .catch(() => { /* keep fallback 5 */ });
  };

  useEffect(() => {
    logActivity({
      activity: "PAGE_VIEW",
      description: `${isOBO ? "OBO-" : ""}DoughSummary Page View`,
      component: `${isOBO ? "OBO." : ""}Page.DoughSummary`,
    });

    if (!didMount.current) {
      getCashSummaryInfo();
      getAvailableWagesAndTipsFromRafil();
      if (!isOBO) {
        getCardStatus();
        if (effectiveUserId) fetchMinCardLoadAmount();
      }
    }

    return () => {
      didMount.current = true;
    };
    // eslint-disable-next-line
  }, []);

  // Get available wages and tips from RafilEarnedController (only for display)
  const getAvailableWagesAndTipsFromRafil = () => {
    if (effectiveUserId && !isOBO) {
      const body = {
        UserId: Number(effectiveUserId),
        LocationId: 1,
      };

      httpService.Get(Employee_Api_EndPoints.EARNED_POINTS, body).then((resp) => {
        // ✅ Handle 500 errors and other error responses gracefully
        if (resp && resp.status === 200 && resp.data && !resp.data.errorFound) {
          setAvailableWagesFromRafil(resp.data?.AvailableWages || 0);
          setAvailableTipsFromRafil(resp.data?.AvailableTips || 0);
        } else {
          // Error response (500, network error, etc.) - set default values
          setAvailableWagesFromRafil(0);
          setAvailableTipsFromRafil(0);
        }
      });
    }
  };

  // ✅ UPDATED: cache transcardAdminNumber from CARD_ACCOUNT_STATUS
  // ✅ FIX: Use httpService.CallWithHeaders (your service exports are CallWithHeaders, Get, Post)
  // ✅ FIX: Proper promise chaining (.then) + pass SecretKey header
  const getCardStatus = () => {
    if (!effectiveUserId) {
      setHasCard(false);
      sessionStorage.removeItem("TranscardAdminNumber");
      return;
    }

    const body = { UserId: Number(effectiveUserId) };

    httpService.Get(Employee_Api_EndPoints.GET_USER_INFO, body).then(
      (resp) => {
        if (resp?.status === 200 && resp.data) {
          const phone =
            resp.data.PhoneNumber || resp.data.phoneNumber || resp.data.phone;

          if (!phone) {
            setHasCard(false);
            sessionStorage.removeItem("TranscardAdminNumber");
            return;
          }

          httpService
            .CallWithHeaders(
              "get",
              Employee_Api_EndPoints.CARD_ACCOUNT_STATUS,
              { phoneNumber: phone },
              { SecretKey: "DoUgHPaYSeCrEKeY" }
            )
            .then((resp2) => {
              if (resp2?.status === 200 && resp2.data) {
                const has = !!resp2.data.hasCard;
                setHasCard(has);
                sessionStorage.setItem("CardRegistrationStatus", has ? "registered" : "not_registered");

                // ✅ store for FastCardBalance usage
                const admin =
                  resp2.data.transcardAdminNumber ||
                  resp2.data.TranscardAdminNumber ||
                  resp2.data.adminNumber ||
                  resp2.data.AdminNumber;

                if (has && admin) {
                  sessionStorage.setItem("TranscardAdminNumber", String(admin));
                } else {
                  sessionStorage.removeItem("TranscardAdminNumber");
                }
              } else {
                setHasCard(false);
                sessionStorage.setItem("CardRegistrationStatus", "not_registered");
                sessionStorage.removeItem("TranscardAdminNumber");
              }
            })
            .catch(() => {
              setHasCard(false);
              sessionStorage.setItem("CardRegistrationStatus", "not_registered");
              sessionStorage.removeItem("TranscardAdminNumber");
            });
        } else {
          setHasCard(false);
          sessionStorage.setItem("CardRegistrationStatus", "not_registered");
          sessionStorage.removeItem("TranscardAdminNumber");
        }
      },
      () => {
        setHasCard(false);
        sessionStorage.setItem("CardRegistrationStatus", "not_registered");
        sessionStorage.removeItem("TranscardAdminNumber");
      }
    );
  };

  const getCashSummaryInfo = () => {
    if (effectiveUserId) {
      const body = {
        UserId: Number(effectiveUserId),
        LocationId: 1,
      };

      logApiCall(
        `${isOBO ? "OBO." : ""}API.GetDoughSummary`,
        `${isOBO ? "OBO-" : ""}Get DoughSummary (request)`
      );

      httpService.Get(Employee_Api_EndPoints.DOUGH_SUMMARY, body).then(
        (resp) => {
          // ✅ Handle 500 errors and other error responses gracefully
          // When pay period is completed, backend may return 500 - display 0 values instead
          // Check if resp is a success response (status 200, no error properties)
          const isSuccessResponse = resp && resp.status === 200 && !resp.response && !resp.code;
          //const isErrorResponse = resp && (resp.response || resp.code);
          
          if (isSuccessResponse && resp.data) {
            // ✅ Success: Normal data received
            const data = resp.data || {};
            setCashSummary({
              wages: data.wages || 0,
              tip: data.tip || 0,
              grossPay: data.grossPay || 0,
              netPay: data.netPay || 0,
            });
            logActivity({
              activity: "API_CALL",
              description: `${isOBO ? "OBO-" : ""}Get DoughSummary (success)`,
              component: `${isOBO ? "OBO." : ""}API.GetDoughSummary`,
            });
          } else {
            // Check if this is a 500 error (completed pay period scenario)
            const errorStatus = resp?.response?.status || resp?.status;
            const is500Error = errorStatus === 500;
            setIsPayPeriodCompleted(is500Error); // Track if pay period is completed
            
            // Error response (500, network error, etc.) - set default values
            // This handles completed pay period scenario gracefully
            setCashSummary({
              wages: 0,
              tip: 0,
              grossPay: 0,
              netPay: 0,
            });
            logActivity({
              activity: "API_CALL",
              description: `${isOBO ? "OBO-" : ""}Get DoughSummary (error - using defaults)`,
              component: `${isOBO ? "OBO." : ""}API.GetDoughSummary`,
            });
          }
          setIsLoad(true);
        },
        (error) => {
          // Error handler - set default values
          console.error("Error fetching dough summary:", error);
          
          // Check if this is a 500 error (completed pay period scenario)
          const is500Error = error && error.response && error.response.status === 500;
          setIsPayPeriodCompleted(is500Error);
          
          setCashSummary({
            wages: 0,
            tip: 0,
            grossPay: 0,
            netPay: 0,
          });
          logActivity({
            activity: "API_CALL",
            description: `${isOBO ? "OBO-" : ""}Get DoughSummary (error - using defaults)`,
            component: `${isOBO ? "OBO." : ""}API.GetDoughSummary`,
          });
          setIsLoad(true);
        }
      );
    } else {
      setIsLoad(true);
      userService.ClearSession();
    }
  };

  const onRangeChange = (event) => {
    event.preventDefault();
    const enterValue = event.target.value.replace(/[^0-9]/g, "");
    if (Number(enterValue) <= Number(netPay || 0)) {
      setRangeValue(Number(enterValue));

      if (showCardLast4Input) {
        setShowCardLast4Input(false);
        setCardLast4("");
      }

      logActivity({
        activity: "INPUT_CHANGE",
        description: `${isOBO ? "OBO-" : ""}Updated Range Value`,
        component: `${isOBO ? "OBO." : ""}Input.RangeSelector`,
      });
    }
  };

  const onSliderChange = (event) => {
    const value = Number(event.target.value);
    if (value <= Number(netPay || 0)) {
      setRangeValue(value);

      if (showCardLast4Input) {
        setShowCardLast4Input(false);
        setCardLast4("");
      }

      logActivity({
        activity: "INPUT_CHANGE",
        description: `${isOBO ? "OBO-" : ""}Updated Range Value via Slider`,
        component: `${isOBO ? "OBO." : ""}Input.RangeSelector`,
      });
    }
  };

  const onCardLast4Change = (event) => {
    const value = event.target.value.replace(/[^0-9]/g, "").slice(0, 4);
    setCardLast4(value);
  };

  const handleLoadMyCard = () => {
    if (!effectiveUserId) {
      userService.ClearSession();
      return;
    }

    // Minimum load amount from backend AppConfig (state set on mount); used for validation/display only

    if (!showCardLast4Input) {
      if (Number(rangeValue) < minimumLoadAmount) {
        setExceptionMsg({
          title: "Error",
          content: `Minimum amount to load is $${minimumLoadAmount}.00. Please enter a valid amount.`,
        });
        setIsException(true);
        return;
      }

      setShowCardLast4Input(true);
      logActivity({
        activity: "INPUT_VALIDATION",
        description: `${isOBO ? "OBO-" : ""}Amount validated, showing card last 4 input`,
        component: `${isOBO ? "OBO." : ""}Input.AmountValidation`,
      });
      return;
    }

    if (cardLast4.length !== 4) {
      setExceptionMsg({
        title: "Error",
        content: "Please enter the last 4 digits of your card.",
      });
      setIsException(true);
      return;
    }

    const body = {
      UserId: Number(effectiveUserId),
      Amount: rangeValue,
      LocationId: 1,
      CardLast4: cardLast4,
      KioskId: "Web",
    };

    setIsLoad(false);

    logApiCall(
      `${isOBO ? "OBO." : ""}API.LoadFunds`,
      `${isOBO ? "OBO-" : ""}Load Funds (request)`
    );

    httpService
      .CallWithHeaders(
        "post",
        Employee_Api_EndPoints.LOAD_FUNDS,
        body,
        { SecretKey: "DoUgHPaYSeCrEKeY" }
      )
      .then(
        (resp) => {
          if (!resp || resp.status !== 200) {
            logActivity({
              activity: "API_CALL",
              description: `${isOBO ? "OBO-" : ""}Load Funds (error)`,
              component: `${isOBO ? "OBO." : ""}API.LoadFunds`,
            });

            // ✅ Check if error is due to last 4 digits mismatch
            let errorMessage = "";
            let isLast4Mismatch = false;

            // Extract error message from response
            if (resp?.response?.data) {
              const errorData = resp.response.data;
              if (typeof errorData === "string") {
                errorMessage = errorData;
              } else if (errorData.message) {
                errorMessage = errorData.message;
              } else if (errorData.title) {
                errorMessage = errorData.title;
              }
            } else if (typeof resp?.data === "string") {
              errorMessage = resp.data;
            }

            // Check if error message indicates last 4 digits mismatch
            const errorMsgLower = errorMessage.toLowerCase();
            isLast4Mismatch = 
              errorMsgLower.includes("last 4") ||
              errorMsgLower.includes("last4") ||
              errorMsgLower.includes("card number") ||
              errorMsgLower.includes("card digits") ||
              errorMsgLower.includes("mismatch") ||
              errorMsgLower.includes("incorrect card") ||
              errorMsgLower.includes("invalid card");

            if (isLast4Mismatch) {
              // ✅ Last 4 digits mismatch - show professional error message on same page
              setIsLoad(true);
              setExceptionMsg({
                title: "Card Verification Failed",
                content: "The last four digits you entered don’t match your card. Please check and try again.",
              });
              setIsException(true);
              // Clear the input so user can re-enter
              setCardLast4("");
              return;
            }

            // ✅ Other errors - navigate to error screen
            // This displays the error screen: "There was an issue loading funds onto your card. Please try again."
            navigate("/makingbreadcard", {
              state: {
                isError: true,
                typeOfTransfer: "loadFundsToCard",
              },
            });
            return;
          }

          logActivity({
            activity: "API_CALL",
            description: `${isOBO ? "OBO-" : ""}Load Funds (success)`,
            component: `${isOBO ? "OBO." : ""}API.LoadFunds`,
          });

          // The LoadFunds call returns a request id. We must poll the
          // GetResponseByRequestId endpoint until Status is Completed
          // (or retries exhausted). The returned payload's `response` field
          // contains the actual JSON with TransferredAmount/ErrorFound.
          const requestId =
            resp?.data?.RequestId || resp?.data?.requestId || resp?.data?.RequestID;

          const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

          const pollResponseByRequestId = async (id, maxRetries = 3, delay = 3000) => {
            let lastStatus = null;
            for (let attempt = 0; attempt < maxRetries; attempt++) {
              try {
                const pollResp = await httpService.CallWithHeaders(
                  "post",
                  Employee_Api_EndPoints.GET_RESPONSE_BY_REQUESTID,
                  { requestID: id },
                  { SecretKey: "DoUgHPaYSeCrEKeY" }
                );

                if (!pollResp || pollResp.status !== 200) {
                  // API call failed; wait and retry
                  if (attempt < maxRetries - 1) await sleep(delay);
                  continue;
                }

                const wrapper = pollResp.data || {};
                const status = wrapper.status || wrapper.Status || "";
                if (status) lastStatus = status;

                // If the remote service returned the inner response as a string, parse it.
                const innerRaw = wrapper.response || wrapper.Response || wrapper.ResponseData;
                let inner = innerRaw;
                if (typeof innerRaw === "string" && innerRaw.trim().length > 0) {
                  try {
                    inner = JSON.parse(innerRaw);
                  } catch (e) {
                    inner = innerRaw;
                  }
                }

                if (status === "Completed") {
                  const errorFound = inner?.ErrorFound === true || !!inner?.ErrorMessage;
                  if (errorFound) {
                    const userMsg = "There was an error loading funds onto your card. Please contact support." || inner?.ErrorMessage;
                    return { success: false, message: userMsg };
                  }

                  const transferredAmt = inner?.TransferredAmount ?? inner?.transferredAmount ?? Number(rangeValue);
                  return { success: true, transferredAmount: Number(transferredAmt) };
                }

                // If still pending/in-progress, retry after delay
                if (status === "Pending" || status === "InProgress") {
                  if (attempt < maxRetries - 1) await sleep(delay);
                  continue;
                }

                // Any other status treat as failure with user-friendly message
                return { success: false, message:  "There was an issue loading funds. Please try again or contact support." ||wrapper?.message };
              } catch (e) {
                if (attempt < maxRetries - 1) await sleep(delay);
                continue;
              }
            }

            // Retries exhausted. If last status indicates still processing, show a polite 'still processing' message.
            if (lastStatus === "Pending" || lastStatus === "InProgress") {
              return {
                success: false,
                message:
                  "Your load request is still processing. Please check again in a few minutes or contact support if the problem persists.",
              };
            }

            return { success: false, message: "Your load request is still processing. Please check again in a few minutes or contact support if the problem persists." };
          };

          if (!requestId) {
            // Fallback to old behavior if no requestId present
            const data = resp?.data || {};
            const transferredAmt =
              (data.TransferredAmount !== undefined && data.TransferredAmount !== null)
                ? Number(data.TransferredAmount)
                : Number(rangeValue);

            navigate("/makingbreadcard", {
              state: {
                bakingValues: { transferredAmount: transferredAmt, previousAvailableWages: Number(netPay || 0) },
                typeOfTransfer: "loadFundsToCard",
              },
            });
            return;
          }

          // Poll the request response
          pollResponseByRequestId(requestId, 3, 3000).then((result) => {
            if (result.success) {
              navigate("/makingbreadcard", {
                state: {
                  bakingValues: {
                    transferredAmount: result.transferredAmount,
                    previousAvailableWages: Number(netPay || 0),
                  },
                  typeOfTransfer: "loadFundsToCard",
                },
              });
            } else {
              setExceptionMsg({ title: "Error", content:   "There was an issue loading funds.Please try again or contact support" || result.message});
              setIsException(true);
              setIsLoad(true);
            }
          });
        },
        (error) => {
          logActivity({
            activity: "API_CALL",
            description: `${isOBO ? "OBO-" : ""}Load Funds (error)`,
            component: `${isOBO ? "OBO." : ""}API.LoadFunds`,
          });

          // ✅ Check if error is due to last 4 digits mismatch
          let errorMessage = "";
          let isLast4Mismatch = false;

          // Extract error message from error response
          if (error?.response?.data) {
            const errorData = error.response.data;
            if (typeof errorData === "string") {
              errorMessage = errorData;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            } else if (errorData.title) {
              errorMessage = errorData.title;
            }
          } else if (typeof error?.data === "string") {
            errorMessage = error.data;
          } else if (error?.message) {
            errorMessage = error.message;
          }

          // Check if error message indicates last 4 digits mismatch
          const errorMsgLower = errorMessage.toLowerCase();
          isLast4Mismatch = 
            errorMsgLower.includes("last 4") ||
            errorMsgLower.includes("last4") ||
            errorMsgLower.includes("card number") ||
            errorMsgLower.includes("card digits") ||
            errorMsgLower.includes("mismatch") ||
            errorMsgLower.includes("incorrect card") ||
            errorMsgLower.includes("invalid card");

          if (isLast4Mismatch) {
            // ✅ Last 4 digits mismatch - show professional error message on same page
            setIsLoad(true);
            setExceptionMsg({
              title: "Card Verification Failed",
              content: "The last 4 digits you entered do not match your card. Please check your card and try again.",
            });
            setIsException(true);
            // Clear the input so user can re-enter
            setCardLast4("");
            return;
          }

          // ✅ Other errors - navigate to error screen
          navigate("/makingbreadcard", {
            state: {
              isError: true,
              typeOfTransfer: "loadFundsToCard",
            },
          });
        }
      );
  };

  const makeBreadCard = logClick(
    `${isOBO ? "OBO.Button.ConfirmBake" : "DPY.Button.LoadMyCard"}`,
    `${isOBO ? "OBO-" : ""}Clicked Load my Card`,
    { onClick: handleLoadMyCard }
  );

  const handleClosePopup = () => {
    setIsException(false);
    // Stay on the current page (Card Load page) instead of navigating to home
    // This allows user to correct their input and retry
  };

  return (
    <Fragment>
      {isLoad && (
        <Fragment>
          <div className="dashboard-container">
            <h2 className="page-module-heading mb-15">{t("DhCashSumm")}</h2>
            {location?.state?.cardRegistered === true && (
              <div className="card-registration-success-banner">
                <strong>{t("cardRegisteredSuccessfully")}</strong>
                <span>{t("canNowLoadFunds")}</span>
              </div>
            )}
            <h2 className="page-module-heading">{t("AvaiTranBaAfterDed")}</h2>

            <Link to="/timesheet" className="timesheet-link">
              <div className="net-pay-container">
                <h1 className={`available-dough-bigger available-wage-tip ${netPay === 0 ? 'zero-amount' : ''}`}>
                  $ {Number(netPay || 0).toFixed(2)}
                </h1>
                <span className="view-timesheet-indicator">
                  <i className="fas fa-clock"></i> View Timesheet
                </span>
              </div>
            </Link>
            {isPayPeriodCompleted && netPay === 0 && (
              <div className="info-message-container" style={{ marginTop: '15px', marginBottom: '15px' }}>
                <p className="info-message">
                  <i className="fas fa-info-circle"></i>
                  Pay period has been completed. New wages will appear when available.
                </p>
              </div>
            )}

            {!isOBO && hasCard === false && (
              <div className="dough-card-draw-option">
                <p className="draw-question-label">
                  {t("readyToGetCard")}
                </p>
                <button
                  className="btn load-card-btn"
                  onClick={() => navigate("/getcardinstructions")}
                >
                  {t("Get a Card")}
                </button>
              </div>
            )}

            {!isOBO && hasCard === true && (
              <div className="dough-card-draw-option">
                <p className="draw-question-label">{t("HowMuchOnDhCrd")}</p>

                <div className="wage-display-container">
                  <div className="wage-tip-container">
                    <span>{t("WageAvai")}</span>
                    <h3 className="available-wage-tip">
                      $
                      {(availableWagesFromRafil > 0
                        ? availableWagesFromRafil
                        : wages
                      ).toFixed(2)}
                    </h3>
                  </div>

                  <div className="wage-tip-container">
                    <span>{t("TipAvai")}</span>
                    <h3 className="available-wage-tip">
                      $
                      {(availableTipsFromRafil > 0
                        ? availableTipsFromRafil
                        : tip
                      ).toFixed(2)}
                    </h3>
                  </div>
                </div>

                <div className="slider-container">
                  <input
                    type="range"
                    min="0"
                    max={Number(netPay || 0)}
                    value={rangeValue}
                    className="slider"
                    id="myRange"
                    onChange={onSliderChange}
                  />
                </div>

                <div className="wage-input-container">
                  <span className="enter-amount-label">{t("EntrAmnt")}</span>
                  <input
                    type="text"
                    className="input-wage"
                    value={Number(rangeValue || 0).toLocaleString()}
                    onChange={onRangeChange}
                  />

                  {showCardLast4Input && (
                    <>
                      <span className="enter-amount-label">
                        Enter last 4 digits of your card
                      </span>
                      <input
                        type="text"
                        className="input-wage"
                        value={cardLast4}
                        onChange={onCardLast4Change}
                        maxLength={4}
                        placeholder="0000"
                      />
                    </>
                  )}

                  <button
                    className="btn load-card-btn"
                    disabled={
                      rangeValue <= 0 ||
                      (showCardLast4Input && cardLast4.length !== 4)
                    }
                    onClick={makeBreadCard}
                  >
                    {t("ConfBake")}
                  </button>

                  <p className="fee-disclaimer">
                    Please note, $3.95 fee is applicable for loading funds onto
                    the card.
                  </p>

                  {!showCardLast4Input &&
                    rangeValue > 0 &&
                    rangeValue < minimumLoadAmount && (
                      <p
                        className="fee-disclaimer"
                        style={{
                          color: "#ff1a1a",
                          marginTop: "10px",
                          fontSize: "15px",
                          fontWeight: 700,
                        }}
                      >
                        ❗Minimum amount to load is ${minimumLoadAmount.toFixed(2)}
                      </p>
                    )}
                </div>
              </div>
            )}
          </div>
        </Fragment>
      )}

      {!isLoad && <LoadingSpinner />}

      {isException && (
        <ExceptionPopup
          title={exceptionMsg.title}
          content={exceptionMsg.content}
          onClose={handleClosePopup}
        />
      )}
    </Fragment>
  );
}

export default DoughSummary;
